import React, { ChangeEvent, KeyboardEvent, useCallback, forwardRef } from 'react';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import SearchIcon from '@material-ui/icons/Search';
import { AnalyticsContext } from '@backstage/core-plugin-api';

/**
 * Props for CustomSearchBar component
 * 
 * @public
 */
export interface CustomSearchBarProps {
  /** Current input value */
  value: string;
  /** Callback when input value changes */
  onChange: (value: string) => void;
  /** Callback when Enter key is pressed or form is submitted */
  onSubmit?: () => void;
  /** Callback when clear button is clicked */
  onClear?: () => void;
  /** Placeholder text for the input */
  placeholder?: string;
  /** Whether to show the clear button when input has value */
  showClearButton?: boolean;
  /** Additional class name for styling */
  className?: string;
}

/**
 * Custom search bar component with filter syntax support.
 * 
 * Features:
 * - GitHub-like search filter syntax (e.g., name:value owner:value)
 * - Clear button that appears when input has value
 * - Accessible with proper aria-labels
 * - Analytics context integration
 * - Search icon as decoration
 * 
 * @public
 */
export const CustomSearchBar = forwardRef<HTMLInputElement, CustomSearchBarProps>(
  (props, ref) => {
    const {
      value,
      onChange,
      onSubmit,
      onClear,
      placeholder = 'Search...',
      showClearButton = true,
      className,
    } = props;

    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onChange(event.target.value);
      },
      [onChange]
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' && onSubmit) {
          onSubmit();
        }
      },
      [onSubmit]
    );

    const handleClear = useCallback(() => {
      onChange('');
      if (onClear) {
        onClear();
      }
    }, [onChange, onClear]);

    const handleClearKeyDown = useCallback(
      (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter') {
          event.stopPropagation();
        }
      },
      []
    );

    return (
      <AnalyticsContext
        attributes={{ pluginId: 'search', extension: 'SearchBar' }}
      >
        <TextField
          data-testid="search-bar-next"
          inputRef={ref}
          fullWidth
          variant="outlined"
          margin="normal"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={className}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton aria-label="Query" size="small" disabled>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: showClearButton && value && (
              <InputAdornment position="end">
                <Button
                  aria-label="Clear"
                  size="small"
                  onClick={handleClear}
                  onKeyDown={handleClearKeyDown}
                >
                  Clear
                </Button>
              </InputAdornment>
            ),
          }}
          inputProps={{
            'aria-label': 'Search',
          }}
        />
      </AnalyticsContext>
    );
  }
);

CustomSearchBar.displayName = 'CustomSearchBar';
